import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Factory, Layers, Package, Users as UsersIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-border rounded-sm p-5" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
            {label}
          </div>
          <div className="mt-3 text-4xl font-display font-bold tracking-tight text-slate-900">
            {value}
          </div>
        </div>
        <div
          className={`w-10 h-10 flex items-center justify-center rounded-sm ${
            accent ? "bg-amber-500 text-slate-900" : "bg-slate-900 text-white"
          }`}
        >
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
          Dashboard
        </div>
        <h1 className="mt-1 text-3xl font-display font-bold tracking-tight text-slate-900">
          Good to see you, {user?.name?.split(" ")[0] || "there"}.
        </h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          This is the foundation your distributor-facing search will run on.
          Each manufacturer you add, each spec you fill in, makes the field
          team faster on every quote.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Factory} label="Manufacturers" value={stats?.manufacturers ?? "—"} accent />
        <StatCard icon={Layers} label="Product Types" value={stats?.product_types ?? "—"} />
        <StatCard icon={Package} label="Products" value={stats?.products ?? "—"} />
        <StatCard icon={UsersIcon} label="Active Users" value={stats?.users ?? "—"} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/manufacturers"
          data-testid="quick-action-manufacturers"
          className="group bg-white border border-border rounded-sm p-6 hover:border-slate-900 transition-colors"
        >
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
            Quick action
          </div>
          <div className="mt-2 text-xl font-display font-semibold text-slate-900 group-hover:text-amber-600 transition-colors">
            Add a manufacturer →
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Create a manufacturer, then define its product types and start
            adding products with custom spec columns.
          </p>
        </Link>
        {user?.role === "admin" ? (
          <Link
            to="/users"
            data-testid="quick-action-users"
            className="group bg-white border border-border rounded-sm p-6 hover:border-slate-900 transition-colors"
          >
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
              Quick action
            </div>
            <div className="mt-2 text-xl font-display font-semibold text-slate-900 group-hover:text-amber-600 transition-colors">
              Invite an editor →
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Add team members who can help build out the product database.
            </p>
          </Link>
        ) : (
          <div className="bg-white border border-border rounded-sm p-6">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
              Your role
            </div>
            <div className="mt-2 text-xl font-display font-semibold text-slate-900">
              Editor
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              You can add and edit manufacturers, product types, and products.
              Ask an admin if you need user-management access.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
